/**
 * Automatisations — port de admin.automatisations.tsx, entièrement branché :
 * table réelle (envoyés/en attente), éditeur complet de chaîne (toutes les
 * étapes, délais, destinataires, variables insérées au curseur), aperçu rendu
 * avec des valeurs d'exemple, activation avec confirmation, PUT réel.
 */
import { useEffect, useRef, useState } from 'react';
import { appelerApi, envoyer } from '../../reseau.ts';
import { Badge, Btn, Card, Input, PageTitle, SectionLabel, Select, Table, Toggle } from '../ui.tsx';

type Etape = { delai_heures: number; sujet: string; corps: string; destinataire?: string };

type Automatisation = {
  id?: string;
  cle: string;
  nom: string;
  declencheur: string;
  actif: boolean;
  etapes: Etape[];
  envoyes?: string;
  en_attente?: string;
};

const DECLENCHEURS = [
  { valeur: 'piste:organisateur_souhaite', texte: 'Piste : organisateur souhaité (vitrine)' },
  { valeur: 'piste:interet_entreprise', texte: 'Piste : intérêt entreprise (vitrine)' },
  { valeur: 'piste:inscription_organisateur', texte: 'Piste : inscription organisateur (vitrine)' },
  { valeur: 'inscription_participant', texte: "Inscription d'un participant" },
  { valeur: 'manuel', texte: 'Déclenchement manuel' },
] as const;

const VARIABLES = [
  '{{demandeur_nom}}',
  '{{demandeur_email}}',
  '{{demandeur_entreprise}}',
  '{{organisateur_nom}}',
  '{{organisateur_email}}',
  '{{evenement_nom}}',
  '{{message}}',
] as const;

const EXEMPLES: Record<string, string> = {
  '{{demandeur_nom}}': 'Pierre Dubois',
  '{{demandeur_email}}': 'pierre@solutions-condroz.be',
  '{{demandeur_entreprise}}': 'Solutions Condroz',
  '{{organisateur_nom}}': 'CCI Liège-Verviers-Namur',
  '{{organisateur_email}}': 'orga@cci-liege.be',
  '{{evenement_nom}}': 'Salon Entreprendre 2026',
  '{{message}}': '« Le networking y est laissé au hasard. »',
};

const rendreExemple = (texte: string) =>
  VARIABLES.reduce((acc, v) => acc.replaceAll(v, EXEMPLES[v]!), texte);

const delaiLisible = (h: number) =>
  h === 0 ? 'J0 — immédiat' : h % 24 === 0 ? `J+${h / 24}` : `+${h} h`;

export function Automatisations() {
  const [liste, setListe] = useState<Automatisation[]>([]);
  const [chaine, setChaine] = useState<Automatisation | null>(null);
  const [etapeActive, setEtapeActive] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const refCorps = useRef<HTMLTextAreaElement>(null);

  const charger = async () => {
    const r = await appelerApi<{ automatisations: Automatisation[] }>('/api/admin/automatisations');
    if (r.donnees) {
      setListe(r.donnees.automatisations);
      setChaine((c) => (c ? (r.donnees!.automatisations.find((a) => a.cle === c.cle) ?? c) : null));
    }
  };

  useEffect(() => {
    void charger();
  }, []);

  const poser = (champ: keyof Automatisation, valeur: unknown) =>
    setChaine((c) => (c ? ({ ...c, [champ]: valeur } as Automatisation) : c));

  const poserEtape = (i: number, champ: keyof Etape, valeur: unknown) =>
    setChaine((c) => {
      if (!c) return c;
      const etapes = [...c.etapes];
      etapes[i] = { ...etapes[i]!, [champ]: valeur } as Etape;
      return { ...c, etapes };
    });

  const inserer = (v: string) => {
    if (!chaine) return;
    const el = refCorps.current;
    const corps = chaine.etapes[etapeActive]?.corps ?? '';
    if (!el) {
      poserEtape(etapeActive, 'corps', corps + v);
      return;
    }
    const debut = el.selectionStart;
    poserEtape(etapeActive, 'corps', corps.slice(0, debut) + v + corps.slice(el.selectionEnd));
  };

  const enregistrer = async (surcharge?: Partial<Automatisation>) => {
    if (!chaine) return;
    const donnees = { ...chaine, ...surcharge };
    if (
      donnees.actif &&
      !chaine.actif &&
      !window.confirm('Activer cette chaîne ? Les e-mails partiront réellement dès la prochaine piste correspondante.')
    ) {
      return;
    }
    const r = await envoyer(
      '/api/admin/automatisations',
      {
        cle: donnees.cle,
        nom: donnees.nom,
        declencheur: donnees.declencheur,
        actif: donnees.actif,
        etapes: donnees.etapes,
      },
      'PUT',
    );
    setMessage(r.erreur ?? 'Chaîne enregistrée.');
    if (!r.erreur) void charger();
  };

  const etape = chaine?.etapes[etapeActive];

  return (
    <>
      <PageTitle
        title="Automatisations"
        subtitle="Les e-mails ne partent réellement que si l'interrupteur de la chaîne est actif. Chaque envoi porte la mention de désinscription."
        action={
          <Btn
            variant="primary"
            onClick={() => {
              setChaine({
                cle: '',
                nom: '',
                declencheur: 'piste:organisateur_souhaite',
                actif: false,
                etapes: [{ delai_heures: 0, sujet: '', corps: '', destinataire: 'organisateur' }],
              });
              setEtapeActive(0);
            }}
          >
            Nouvelle chaîne
          </Btn>
        }
      />

      <Table head={['Chaîne', 'Déclencheur', 'Étapes', 'Envoyés', 'En attente', 'Actif']}>
        {liste.map((a) => (
          <tr
            key={a.cle}
            className="cursor-pointer"
            onClick={() => {
              setChaine(a);
              setEtapeActive(0);
            }}
          >
            <td className="text-blanc">{a.nom}</td>
            <td className="text-fumee">{DECLENCHEURS.find((d) => d.valeur === a.declencheur)?.texte ?? a.declencheur}</td>
            <td className="text-blanc">{a.etapes.length}</td>
            <td className="text-blanc">{a.envoyes ?? 0}</td>
            <td className="text-blanc">{a.en_attente ?? 0}</td>
            <td>
              <Toggle on={a.actif} />
            </td>
          </tr>
        ))}
        {liste.length === 0 && (
          <tr>
            <td colSpan={6} className="text-fumee">
              Aucune chaîne — créez la première avec « Nouvelle chaîne ».
            </td>
          </tr>
        )}
      </Table>

      {chaine && (
        <>
          <h2 className="display-title mt-10 text-[24px] text-blanc">
            Éditeur — {chaine.nom || 'nouvelle chaîne'}
          </h2>

          <div className="mt-5 grid gap-4 lg:grid-cols-2">
            <div className="flex flex-col gap-4">
              <Card>
                <div className="grid gap-4 sm:grid-cols-3">
                  <Input label="Clé technique" value={chaine.cle} onChange={(v) => poser('cle', v)} placeholder="annonce-organisateur" />
                  <Input label="Nom de la chaîne" value={chaine.nom} onChange={(v) => poser('nom', v)} placeholder="Annonce à l'organisateur" />
                  <Select label="Déclencheur" value={chaine.declencheur} onChange={(v) => poser('declencheur', v)} options={DECLENCHEURS} />
                </div>
              </Card>

              <div className="relative pl-6">
                <span className="absolute bottom-6 left-[7px] top-6 w-px bg-graphite" />
                <div className="flex flex-col gap-4">
                  {chaine.etapes.map((e, i) => (
                    <div key={i} className="relative">
                      <span
                        className={`absolute -left-6 top-6 h-3.5 w-3.5 rounded-full border ${
                          i === etapeActive ? 'bg-match-gradient border-transparent' : 'border-graphite bg-void'
                        }`}
                      />
                      <Card className={i === etapeActive ? 'ring-1 ring-graphite' : ''}>
                        <button className="flex w-full flex-wrap items-center justify-between gap-3 text-left" onClick={() => setEtapeActive(i)}>
                          <SectionLabel>
                            Étape {i + 1} · {delaiLisible(e.delai_heures)}
                          </SectionLabel>
                          <Badge>{e.destinataire === 'demandeur' ? 'Le demandeur' : "L'organisateur"}</Badge>
                        </button>

                        {i === etapeActive ? (
                          <div className="mt-4 flex flex-col gap-4">
                            <div className="grid gap-4 sm:grid-cols-2">
                              <Input
                                label="Délai (heures après le déclencheur)"
                                type="number"
                                value={String(e.delai_heures)}
                                onChange={(v) => poserEtape(i, 'delai_heures', Math.max(0, Number(v) || 0))}
                              />
                              <Select
                                label="Destinataire"
                                value={e.destinataire ?? 'organisateur'}
                                onChange={(v) => poserEtape(i, 'destinataire', v)}
                                options={[
                                  { valeur: 'organisateur', texte: "L'organisateur (prospection)" },
                                  { valeur: 'demandeur', texte: 'Le demandeur (accueil, suivi)' },
                                ]}
                              />
                            </div>
                            <Input label="Sujet" value={e.sujet} onChange={(v) => poserEtape(i, 'sujet', v)} placeholder="{{demandeur_nom}} a demandé EventMatch à votre prochain événement" />
                            <label className="block">
                              <span className="label-caps block text-fumee">Corps</span>
                              <textarea
                                ref={refCorps}
                                value={e.corps}
                                onChange={(ev) => poserEtape(i, 'corps', ev.target.value)}
                                rows={7}
                                className="field-input mt-2 resize-y !text-sm"
                              />
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {VARIABLES.map((v) => (
                                <button
                                  key={v}
                                  onClick={() => inserer(v)}
                                  className="rounded-full border border-graphite px-3 py-1.5 font-mono text-[12px] text-fumee transition-colors hover:text-blanc"
                                >
                                  {v}
                                </button>
                              ))}
                            </div>
                            {chaine.etapes.length > 1 && (
                              <button
                                onClick={() => {
                                  poser('etapes', chaine.etapes.filter((_, j) => j !== i));
                                  setEtapeActive(0);
                                }}
                                className="self-start text-[13px] text-fumee hover:text-blanc"
                              >
                                Retirer cette étape
                              </button>
                            )}
                          </div>
                        ) : (
                          <p className="mt-3 text-[15px] text-blanc">{e.sujet || <span className="text-fumee">(sans sujet)</span>}</p>
                        )}
                      </Card>
                    </div>
                  ))}

                  <Btn
                    onClick={() => {
                      poser('etapes', [
                        ...chaine.etapes,
                        { delai_heures: 72, sujet: '', corps: '', destinataire: 'organisateur' },
                      ]);
                      setEtapeActive(chaine.etapes.length);
                    }}
                  >
                    Ajouter une étape
                  </Btn>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <Card>
                <SectionLabel>Aperçu rendu — étape {etapeActive + 1}</SectionLabel>
                <div className="mt-4 rounded-[10px] border border-graphite p-5">
                  <p className="text-[11px] uppercase tracking-[0.05em] text-fumee">
                    à : {etape?.destinataire === 'demandeur' ? EXEMPLES['{{demandeur_email}}'] : EXEMPLES['{{organisateur_email}}']}
                  </p>
                  <p className="mt-3 text-[17px] font-light text-blanc">
                    {rendreExemple(etape?.sujet ?? '') || '(sujet vide)'}
                  </p>
                  <p className="mt-4 whitespace-pre-line text-[14px] leading-relaxed text-fumee">
                    {rendreExemple(etape?.corps ?? '') || '(corps vide)'}
                  </p>
                  <p className="mt-5 border-t border-graphite pt-3 text-[12px] leading-relaxed text-fumee">
                    —{'\n'}EventMatch.pro — networking événementiel, offert par Elite Lead. Pour ne
                    plus recevoir nos messages, répondez « stop ».
                  </p>
                </div>
              </Card>

              <Card>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-[15px] text-blanc">Active — les e-mails partent réellement</p>
                    <p className="mt-1 text-[13px] text-fumee">Une confirmation est demandée à l'activation.</p>
                  </div>
                  <button
                    onClick={() => {
                      const actif = !chaine.actif;
                      poser('actif', actif);
                      void enregistrer({ actif });
                    }}
                    aria-pressed={chaine.actif}
                  >
                    <Toggle on={chaine.actif} />
                  </button>
                </div>
              </Card>

              {message && (
                <p className="text-[13px] text-fumee" role="status">
                  {message}
                </p>
              )}

              <div className="flex gap-3">
                <Btn variant="primary" onClick={() => void enregistrer()}>
                  Enregistrer la chaîne
                </Btn>
                <Btn onClick={() => setChaine(null)}>Fermer</Btn>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
